import React, {
  FunctionComponent,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { TimelineItem } from "../../../../store/timelines/types";
import { PlayerState } from "../../../../store/rootReducer";
import { PulseItem } from "../../../../store/pulse/types";
import { Logger } from "../../../../logger/logger";
import {
  DynamicViewerProps,
  DynamicRenderSlot,
} from "./types/dynamic-viewer-types";
import { useTimeoutManager } from "./hooks/useTimeoutManager";
import { useSlotManager } from "./hooks/useSlotManager";
import { DynamicSlot } from "./components/DynamicSlot";

const log = new Logger("DynamicViewer");

export const DynamicViewer: FunctionComponent<DynamicViewerProps> = memo(
  ({
    zoneId,
    timelineItem,
    isPreload,
    onContentFailure,
    timelineItemIndex,
    timeOptions,
    mainTimelineId,
  }) => {
    const [currentIndex, setCurrentIndex] = useState(() => {
      const initialIndex = timelineItem.currentIdx || 0;
      return initialIndex;
    });
    const renderSlotsRef = useRef<DynamicRenderSlot[]>([]);

    // Get the bpItems from the timeline item
    const bpItems = useMemo(() => timelineItem.bpItems || [], [
      timelineItem.bpItems,
    ]);

    // DERIVE EVERYTHING FROM currentIndex - no additional state
    const currentBpItem = useMemo((): TimelineItem | undefined => {
      return bpItems.length > 0 ? bpItems[currentIndex] : undefined;
    }, [bpItems, currentIndex]);

    const nextBpItem = useMemo((): TimelineItem | undefined => {
      if (bpItems.length === 0 || bpItems.length === 1) return undefined;
      const nextIndex = (currentIndex + 1) % bpItems.length;
      return bpItems[nextIndex];
    }, [bpItems, currentIndex]);

    const pulseItemState = useSelector<
      PlayerState,
      {
        updateItem: PulseItem | undefined;
        finishedItem: PulseItem | undefined;
      }
    >((state) => {
      const pulseItem = state.pulse.byId[`${currentBpItem?.id}-${zoneId}`];
      return {
        updateItem:
          pulseItem?.type === "update" && pulseItem.playbackWillFinishInMs > 0
            ? pulseItem
            : undefined,
        finishedItem: pulseItem?.type === "finished" ? pulseItem : undefined,
      };
    });

    const pulseItemForZone = pulseItemState.updateItem;
    const finishedPulseItemForZone = pulseItemState.finishedItem;
    // Handle advancing to next item
    const handleAdvanceToNext = useCallback(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % bpItems.length;
        return nextIndex;
      });
    }, [bpItems]);

    // Use custom hooks for complex logic
    useTimeoutManager({
      currentBpItem,
      nextBpItem,
      bpItems,
      isPreload,
      timelineItem,
      pulseItemForZone: finishedPulseItemForZone,
      onAdvanceToNext: handleAdvanceToNext,
    });

    // Note: finishedPulseItemForZone is now handled in useTimeoutManager
    // for immediate advancement. This selector is available for other purposes
    // if you need to handle finished events separately from advancement logic.

    const renderSlots = useSlotManager({
      bpItems,
      currentIndex,
      currentBpItem,
      nextBpItem,
      timeOptions,
      renderSlotsRef,
    });

    const handleContentFailure = useCallback(() => {
      log.warn({
        message: "DynamicViewer: Content failure occurred",
        context: {
          currentIndex,
          currentBpItemId: currentBpItem?.id,
          totalBpItems: bpItems.length,
        },
      });

      // Move to next bpItem on failure (only if there are multiple items)
      if (bpItems.length > 1) {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % bpItems.length);
      } else {
        log.warn({
          message:
            "DynamicViewer: Content failure but only 1 item available - cannot advance",
          context: {
            currentIndex,
            currentBpItemId: currentBpItem?.id,
            totalBpItems: bpItems.length,
          },
        });
      }
    }, [currentIndex, currentBpItem, bpItems]);

    // If not dynamic duration or no bpItems, render nothing
    if (!timelineItem.isDynamicDuration || bpItems.length === 0) {
      return null;
    }

    return (
      <>
        {renderSlots.map((slot, idx) => (
          <DynamicSlot
            key={idx}
            slot={slot}
            index={idx}
            zoneId={zoneId}
            timelineItemIndex={timelineItemIndex}
            pulseItemForZone={
              pulseItemForZone?.zoneId === zoneId ? pulseItemForZone : undefined
            }
            mainTimelineId={mainTimelineId}
            currentBpItem={currentBpItem}
            onContentFailure={handleContentFailure}
          />
        ))}
      </>
    );
  }
);

DynamicViewer.displayName = "DynamicViewer";
